import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { Wallet, ExternalLink, Trash2, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { StripeConnectCard } from "./payments/StripeConnectCard";
import { PAYOUT_SETUP_ANCHOR } from "./payments/payoutSetupAnchor";
import { StripeTransactions } from "./payments/StripeTransactions";

// RETIRED processors (PayPal: 2026-08-03, decision "Stripe end to end"). No new
// connection can be made from this page; a card renders ONLY for a practice that already
// has the connection, because the backend still returns it and a practitioner must keep a
// way to disconnect. Each card is therefore an exit, never an entry: it offers
// Disconnect and nothing that starts a provider flow.
const RETIRED_PROCESSORS = {
  paypal: {
    name: "PayPal",
    color: "from-[#003087] to-[#009cde]",
    descKey: "pay.paypal.desc",
    docs: "https://developer.paypal.com/",
  },
};

/**
 * PaymentsAdmin — Page tenant /admin/payments
 * Stripe est le seul rail : Stripe Connect pour encaisser sur le compte du praticien
 * (StripeConnectCard) et le compte plateforme pour le reste (StripeTransactions).
 * Square a été retiré le 2026-08-04 ; PayPal est dormant et n'apparaît que pour un
 * praticien DÉJÀ branché, afin qu'il puisse se débrancher.
 */
export default function PaymentsAdmin() {
  const t = useT("admin");
  const [conns, setConns] = useState(null);
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/payments/connections");
      setConns(data);
    } catch (e) {
      toast.error(extractApiError(e, t("pay.error")));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Toast on redirect from OAuth callback
  useEffect(() => {
    if (searchParams.get("paypal") === "connected") toast.success(t("pay.paypalConnected"));
  }, [searchParams, t]);

  const disconnect = async (processor) => {
    if (!window.confirm(t("pay.confirmDisconnect", { name: RETIRED_PROCESSORS[processor].name }))) return;
    try {
      await api.delete(`/tenant/payments/connections/${processor}`);
      toast.success(t("pay.disconnected"));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("pay.error")));
    }
  };

  if (!conns) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-eh-muted" /></div>;

  return (
    <div data-testid="payments-admin" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <Wallet className="w-6 h-6 text-sol-gold" /> {t("pay.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">
          {t("pay.subtitle")}
        </p>
      </div>

      {/* THE ANCHOR the wallet's "your payout destination is not ready" note links to
          (BUG-222). On a wrapper rather than inside the card, so the target exists while
          the card is still loading and while it is rendering its own error state — a link
          that resolves only on the happy path is a link that sometimes does nothing. */}
      <div id={PAYOUT_SETUP_ANCHOR} className="scroll-mt-6">
        <StripeConnectCard />
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {Object.entries(RETIRED_PROCESSORS).filter(([proc]) => conns[proc]?.connected).map(([proc, meta]) => {
          const c = conns[proc];
          const blocked = c.blocked;
          return (
            <div key={proc} className="bg-white rounded-2xl border border-eh-border-soft overflow-hidden" data-testid={`pay-card-${proc}`}>
              <div className={`h-2 bg-gradient-to-r ${meta.color}`} />
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl">{meta.name}</h3>
                  {blocked ? (
                    <span className="text-xs bg-rose-100 text-rose-700 px-2.5 py-0.5 rounded-full" data-testid={`pay-blocked-${proc}`}>{t("pay.blockedBySolunary")}</span>
                  ) : (
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {t("pay.connected")}
                    </span>
                  )}
                </div>
                <p className="text-sm text-eh-muted mb-4">{t(meta.descKey)}</p>
                {!blocked && (
                  <div className="text-xs text-eh-muted mb-4 space-y-1">
                    {c.merchant_id && <div>{t("pay.merchant")} <code className="bg-eh-cream/40 px-1.5 py-0.5 rounded">{c.merchant_id}</code></div>}
                    {c.environment && <div>{t("pay.environment")} <code className="bg-eh-cream/40 px-1.5 py-0.5 rounded">{c.environment}</code></div>}
                  </div>
                )}
                <div className="flex gap-2">
                  {blocked ? (
                    <span className="text-xs text-rose-600 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {t("pay.unavailableShort")}</span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => disconnect(proc)} data-testid={`disconnect-${proc}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> {t("pay.disconnect")}
                    </Button>
                  )}
                  <a href={meta.docs} target="_blank" rel="noopener noreferrer" className="text-xs text-eh-muted hover:text-eh-ink inline-flex items-center self-center">
                    {t("pay.docs")} <ExternalLink className="w-3 h-3 ml-0.5" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm" data-testid="payments-info">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <strong>{t("pay.howItWorks")}</strong>{t("pay.howItWorksText")}
          </div>
        </div>
      </div>

      <StripeTransactions />
    </div>
  );
}
